<template>
  <div class="unmapped">

    <header class="masthead">
      <h1 class="title">Unmapped badges<span class="dot">.</span></h1>
      <p class="sub">
        Cards that have opened a door but belong to nobody Vero knows. Their
        taps are recorded, but they show on no board and count for no one until
        a person is assigned here.
      </p>
    </header>

    <p v-if="loading" class="msg">Reading badge events</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="badges.length === 0" class="msg">
      Every badge that has tapped a door is mapped to somebody.
    </p>

    <table v-else class="table">
      <thead>
        <tr>
          <th>Badge</th>
          <th>Last seen</th>
          <th>Taps</th>
          <th>Assign to</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="b in badges" :key="b.badge_uuid">
          <td class="mono">{{ b.badge_uuid }}</td>

          <td>
            {{ formatWhen(b.last_seen) }}
            <span class="where">
              {{ b.last_direction === 'ENTERED' ? 'in' : 'out' }}
              <template v-if="b.last_office">at {{ b.last_office }}</template>
              <template v-if="b.last_country">({{ b.last_country }})</template>
            </span>
          </td>

          <td class="num">
            {{ b.events }}
            <span class="since">since {{ formatWhen(b.first_seen) }}</span>
          </td>

          <td>
            <!-- A type-ahead rather than a 240-entry dropdown. The admin
                 usually knows whose card it is; they need to find the name,
                 not scroll to it. -->
            <input
              class="input"
              type="text"
              list="people"
              placeholder="Start typing a name"
              :value="draft[b.badge_uuid] ?? ''"
              @input="draft[b.badge_uuid] = $event.target.value"
            >
          </td>

          <td class="actions">
            <span v-if="state[b.badge_uuid]" class="state">{{ state[b.badge_uuid] }}</span>
            <button
              class="btn"
              :disabled="!personFor(b.badge_uuid) || saving === b.badge_uuid"
              @click="assign(b)"
            >{{ saving === b.badge_uuid ? 'Saving…' : 'Assign' }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <datalist id="people">
      <option v-for="p in people" :key="p.id" :value="p.name">{{ p.email }}</option>
    </datalist>

    <p v-if="!loading && !error && badges.length > 0" class="foot">
      Taps from a badge that is later assigned are kept and attributed to that
      person, including the ones already recorded.
    </p>

  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import { getUnmappedBadges } from '@/services/UnmappedService'
import { setBadgeMapping } from '@/services/BadgeService'

const badges = ref([])
const people = ref([])
const draft = ref({})
const state = ref({})

const loading = ref(true)
const error = ref(null)
const saving = ref(null)

function flash(key, value) {
  state.value = { ...state.value, [key]: value }
  setTimeout(() => {
    const next = { ...state.value }
    if (next[key] === value) delete next[key]
    state.value = next
  }, 2000)
}

/** The person whose name is typed exactly, or null. Exact so a half-typed
    name cannot assign the wrong colleague. */
function personFor(badgeUuid) {
  const typed = (draft.value[badgeUuid] ?? '').trim().toLowerCase()
  if (!typed) return null
  return people.value.find((p) => p.name.toLowerCase() === typed) ?? null
}

function formatWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'Z')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  })
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [list, hierarchy] = await Promise.all([
      getUnmappedBadges(),
      getSiteHierarchy()
    ])

    badges.value = list

    // Everyone in every country, so a card tapped in Denmark can be assigned
    // to somebody whose usageLocation is Singapore, which visitors do.
    const all = []
    for (const country of hierarchy?.countries ?? []) {
      const inCountry = await getPeopleInCountry(country.code).catch(() => [])
      all.push(...(inCountry ?? []))
    }
    people.value = all.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function assign(badge) {
  const person = personFor(badge.badge_uuid)
  if (!person) return

  saving.value = badge.badge_uuid

  try {
    await setBadgeMapping({
      badgeUuid: badge.badge_uuid,
      entraObjectId: person.id,
      displayName: person.name,
      email: person.email
    })

    // Gone from this list by definition: the query finds badges by the absence
    // of a mapping, and there is one now.
    badges.value = badges.value.filter((b) => b.badge_uuid !== badge.badge_uuid)
  } catch (err) {
    console.error(err)
    flash(badge.badge_uuid, err.message ?? 'failed')
  } finally {
    saving.value = null
  }
}

onMounted(load)
</script>


<style scoped>
.unmapped {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;

  max-width: 1100px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 64ch;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14.5px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 0 12px 10px 0;
  border-bottom: 1px solid var(--line);
}

.table td {
  padding: 12px 12px 12px 0;
  border-bottom: 1px solid #f1f4f7;
  vertical-align: middle;
}

.mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13px;
  color: var(--ink);
  word-break: break-all;
}

.where,
.since {
  display: block;
  font-size: 12.5px;
  color: #a3adb9;
  margin-top: 2px;
}

.num { font-variant-numeric: tabular-nums; }

.input {
  font-family: inherit;
  font-size: 14px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  min-width: 220px;
}

.input:focus { border-color: var(--ink); }

.actions { text-align: right; white-space: nowrap; }

.state {
  font-size: 12.5px;
  color: #b3261e;
  margin-right: 12px;
}

.btn {
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--ink);
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  cursor: pointer;
}

.btn:hover:not(:disabled) { background: #00548f; }
.btn:disabled { opacity: 0.35; cursor: default; }

.foot {
  font-size: 13.5px;
  color: var(--ink-soft);
  margin: 20px 0 0;
  max-width: 60ch;
}

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
  max-width: 60ch;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 800px) {
  .unmapped { padding: 0 20px 60px; }
}
</style>
